import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import prairiLogo from '../assets/prairiwhite.png';
import cryingUnicorn from '../assets/crying_unicorn.png';

const REDIRECT_COUNTDOWN_SECONDS = 5;

const DELETE_REASONS = [
  { value: 'no-value', label: "I/We don't see enough value to be on your service." },
  {
    value: 'received-funding',
    label:
      "We received our funding on PRAIRI (or elsewhere) and don't envision ever needing a future round of funding.",
  },
  { value: 'poor-matches', label: 'Too few or not enough quality matches.' },
  { value: 'other', label: 'Other reason:' },
];

const RemoveProfilePage: React.FC = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [reason, setReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeletionSuccess, setShowDeletionSuccess] = useState(false);
  const [deletionCountdown, setDeletionCountdown] = useState(REDIRECT_COUNTDOWN_SECONDS);

  useEffect(() => {
    if (!showDeletionSuccess || deletionCountdown <= 0) return;
    const t = setInterval(() => {
      setDeletionCountdown(prev => {
        if (prev <= 1) {
          clearInterval(t);
          signOut().then(() => navigate('/', { replace: true }));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [showDeletionSuccess, deletionCountdown, signOut, navigate]);

  const handleDeleteAccount = async () => {
    if (!reason) {
      setError('Please select a reason for deleting your account.');
      return;
    }

    if (reason === 'other' && !otherReason.trim()) {
      setError('Please specify your reason.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const deleteUserAccountFn = httpsCallable(functions, 'deleteUserAccount');

      const finalReason = reason === 'other' ? otherReason : reason;

      const result = await deleteUserAccountFn({
        confirmDelete: true,
        deleteReason: finalReason,
      });

      const data = result.data as { success: boolean; error?: string };

      if (data.success) {
        setShowDeletionSuccess(true);
        setDeletionCountdown(REDIRECT_COUNTDOWN_SECONDS);
      } else {
        setError(data.error || 'Failed to delete account. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('An error occurred while deleting your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (showDeletionSuccess) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100000] p-4">
        <div className="bg-dark-900 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border border-emerald-500/30">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <svg
                className="w-10 h-10 text-emerald-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4 text-center">
            Account deleted successfully
          </h2>
          <p className="text-gray-300 text-center mb-2">
            Your account has been deleted successfully.
          </p>
          <p className="text-gray-300 text-center mb-2">
            You&apos;ll be redirected in{' '}
            <span className="font-mono text-white font-semibold">{deletionCountdown}</span> second
            {deletionCountdown !== 1 ? 's' : ''}…
          </p>
          <p className="text-gray-500 text-sm text-center">You are now logged out.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-950 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-xl bg-[#1e1e1e] rounded-[24px] p-10 shadow-2xl border border-gray-800 relative">
        <div className="flex flex-col items-center">
          {/* Crying Unicorn Illustration */}
          <div className="w-32 h-32 mb-8 flex items-center justify-center">
            {/* Note: User should replace this with the actual crying unicorn asset */}
            <img
              src={cryingUnicorn}
              alt="Crying Unicorn"
              className="w-full h-full object-contain opacity-90"
            />
          </div>

          <h1 className="text-2xl font-bold text-white mb-4">Remove/Delete your match profile</h1>

          <p className="text-gray-400 text-left w-full mb-6">
            Reason I wish to delete my Data Profile and account:
          </p>

          <div className="w-full space-y-4 mb-8">
            {DELETE_REASONS.map(option => (
              <div key={option.value} className="flex flex-col">
                <label className="flex items-start cursor-pointer text-gray-300 hover:text-white transition-colors">
                  <div className="relative flex items-center justify-center mt-1 mr-3">
                    <input
                      type="radio"
                      name="delete-reason"
                      value={option.value}
                      checked={reason === option.value}
                      onChange={e => {
                        setReason(e.target.value);
                        setError(null);
                      }}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded-full border-2 transition-all ${
                        reason === option.value
                          ? 'border-emerald-500 bg-emerald-500'
                          : 'border-gray-600 bg-transparent'
                      }`}
                    >
                      {reason === option.value && (
                        <div className="w-2 h-2 bg-white rounded-full absolute top-1.5 left-1.5"></div>
                      )}
                    </div>
                  </div>
                  <span className="text-left text-sm md:text-base leading-relaxed">
                    {option.label}
                  </span>
                </label>

                {option.value === 'other' && reason === 'other' && (
                  <div className="mt-3 ml-8">
                    <input
                      type="text"
                      value={otherReason}
                      onChange={e => setOtherReason(e.target.value)}
                      placeholder="Please specify..."
                      className="w-full bg-[#2a2a2a] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {error && (
            <div className="w-full mb-6 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm text-center">
              {error}
            </div>
          )}

          <div className="flex items-center gap-4 w-full">
            <button
              onClick={handleDeleteAccount}
              disabled={loading}
              className="flex-1 py-3 px-6 rounded-xl border-2 border-red-600 text-white font-bold hover:bg-red-600 transition-all disabled:opacity-50 text-sm uppercase tracking-wider"
            >
              {loading ? 'Processing...' : 'Delete My Data Profile & Account'}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="py-3 px-8 rounded-xl bg-gray-700 text-gray-300 font-bold hover:bg-gray-600 hover:text-white transition-all text-sm uppercase tracking-wider"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoveProfilePage;
